import type { Community } from '@communities/community/domain';
import { CommunityGrid } from '@communities/community-groups/components';
import { CommunityGroupHeader } from '@communities/community-groups/components/CommunityGroupHeader';
import type { CommunityGroup } from '@communities/community-groups/domain';
import { CommunityHeader, SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Section, PageHeader, Button, ButtonType, ButtonSize, OverlayMode } from '@soteria/design-system-ve';

import * as styles from './CommunityGroupHeaderSection.css';

interface CommunityGroupHeaderSectionProps {
  communityGroup: CommunityGroup;
  communities: Community[] | null;
  isCommunityGroupAdmin: boolean;
  onAddCommunity: () => void;
  onSelectCommunity: (community: Community) => void;
}

/**
 * Header section component with gradient transition
 * Contains community group name, description, and communities grid
 * Background image is handled by the global HeroBackground component
 */
function CommunityGroupHeaderSectionComponent({
  communityGroup,
  communities,
  isCommunityGroupAdmin,
  onAddCommunity,
  onSelectCommunity,
}: CommunityGroupHeaderSectionProps) {
  // Action buttons for V2 header
  const actionButtons = isCommunityGroupAdmin ? (
    <Button buttonSize={ButtonSize.Large} buttonType={ButtonType.Brand} overlayMode={OverlayMode.Solid} onClick={onAddCommunity}>
      ADD COMMUNITY
    </Button>
  ) : undefined;

  // Communities grid component shared between V1 and V2
  const communitiesGrid = (
    <div className={styles.communityGridStyle}>
      <CommunityGrid
        columnFit="auto-fill"
        communities={communities || []}
        emptyStateClassName={styles.emptyCommunitiesStateStyle}
        onSelectCommunity={onSelectCommunity}
      />
    </div>
  );

  // V1: Original implementation
  const v1Component = (
    <div className={styles.heroSection} data-testid="community-group-header-v1">
      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Community Group Header */}
        <CommunityGroupHeader
          description={communityGroup.description || ''}
          isCommunityGroupAdmin={isCommunityGroupAdmin}
          name={communityGroup.name}
          onAddCommunity={onAddCommunity}
        />

        {/* Communities Section */}
        <Section className={styles.communitiesSection} spacing="none">
          <SectionHeader description="Communities in this group." title="Communities" />
          {communitiesGrid}
        </Section>
      </div>
    </div>
  );

  // V2: New design system component with communities section moved outside
  const v2Component = (
    <>
      <PageHeader data-testid="community-group-header-v2" subtitle={communityGroup.description || undefined} title={communityGroup.name}>
        {actionButtons}
      </PageHeader>

      {/* Communities Section - moved outside header in V2 */}
      <div className={styles.communitiesSectionV2}>
        <Section spacing="none">
          <SectionHeader description="Communities in this group." title="Communities" />
          {communitiesGrid}
        </Section>
      </div>
    </>
  );

  return <CommunityHeader v1Component={v1Component} v2Component={v2Component} />;
}

export const CommunityGroupHeaderSection = withAppInsightsTracking(CommunityGroupHeaderSectionComponent, 'CommunityGroupHeaderSection');
